import { MapMouseEvent } from 'shared/map-exports';
import { useActiveEvacZonesQuery } from 'features/evacZones/hooks/useActiveEvacZonesQuery';
import { useExternalFeatureLayers } from 'features/mapping/hooks/useExternalFeatureLayers';
import { useAQIStationsLayerEnabled } from 'features/mapping/hooks/useAQIStationsLayerEnabled';
import { useLocationV2Enabled } from 'features/locations/hooks/useLocationV2Enabled';
import { useExternalGeoEvents } from 'hooks/useExternalGeoEvents';
import { useReleaseFlag } from 'hooks/useReleaseFlags';
import { useMapMode } from 'hooks/useMapMode';
import { resolveClick } from 'features/mapping/mapClick/resolution';
import { useLocation } from 'react-router-dom';
import { useGeoEvent } from 'features/geoEvents/hooks/useGeoEvent';
import { useSelectedGeoEventId } from 'features/geoEvents/hooks/useSelectedGeoEventId';
import { AnyGeoEvent } from 'features/geoEvents/anyGeoEvent.types';
import { useOverlappingFeaturesState } from 'state/useOverlappingFeaturesState';
import { shouldIgnoreMapClick } from './interactiveFeatures';
import { getIsPointRouterEntitySelected } from './featureClassification';
import { buildClickCandidates } from './candidates';
import { gatherClickFeatures } from './gather';
import { useExecuteClickResolution } from './execute';

type MapClickHandler = (e: MapMouseEvent) => void;

export const useMapClickHandler = (
  onClick?: MapClickHandler,
): MapClickHandler => {
  const activeEvacZones = useActiveEvacZonesQuery();
  const { externalGeoEvents } = useExternalGeoEvents();
  const { externalFeatureLayers } = useExternalFeatureLayers();
  const showLocationV2 = useLocationV2Enabled();
  const aqiStationsLayerEnabled = useAQIStationsLayerEnabled();
  const pinDropImprovementsEnabled = useReleaseFlag('pin_drop_improvements');
  const location = useLocation();
  const { geoEvent: selectedGeoEvent } = useGeoEvent<AnyGeoEvent>({
    id: useSelectedGeoEventId(),
    useCacheBuster: true,
  });
  const { clearOverlappingFeatures } = useOverlappingFeaturesState();
  const { isTimelineMode, setMapMode } = useMapMode();
  const executeClickResolution = useExecuteClickResolution();

  const handleMapClick: MapClickHandler = (event) => {
    if (shouldIgnoreMapClick(event)) {
      return;
    }

    const featuresWithEntityType = gatherClickFeatures(event, {
      externalFeatureLayers,
      showLocationV2,
      aqiStationsLayerEnabled,
      pathname: location.pathname,
      pinDropImprovementsEnabled,
    });

    if (!featuresWithEntityType.length) {
      clearOverlappingFeatures();
      onClick?.(event);
      return;
    }

    // Clicking on any interactive feature exits timeline mode
    if (isTimelineMode) setMapMode('default');

    // Approved reports are visible globally, so clicks must resolve without a selected incident.
    // All reportable types share the `/i` prefix, so 'wildfire' is a safe fallback.
    const reportGeoEventType =
      selectedGeoEvent && selectedGeoEvent.geoEventType !== 'location'
        ? selectedGeoEvent.geoEventType
        : 'wildfire';

    // Build candidates + targets; resolveClick makes the interaction decision, the handler
    // just executes it.
    const { candidates, targets } = buildClickCandidates({
      features: featuresWithEntityType,
      navigationalContext: { route: location, reportGeoEventType },
      activeEvacZones,
      externalGeoEvents,
    });

    const resolution = resolveClick(candidates, {
      pointRouterEntityOpen: getIsPointRouterEntitySelected(location.pathname),
    });

    executeClickResolution(resolution, { targets });

    onClick?.(event);
  };

  return handleMapClick;
};
